import type * as z from "zod";
import type { AIProviderId } from "../../shared/aiProviders";
import type { attachmentSchema } from "../../shared/mutators/attachmentValidation";
import {
  attachmentSizeLimit as sharedAttachmentSizeLimit,
  resolveAttachmentMediaType as sharedResolveAttachmentMediaType,
} from "../../shared/attachmentPolicy";
import { apiUrl } from "./tenantOrigin";
export const MAX_ATTACHMENTS = 5;

export function resolveAttachmentMediaType(
  file: Pick<File, "name" | "type">,
  providerId: AIProviderId
) {
  return sharedResolveAttachmentMediaType(file, providerId);
}

export function attachmentSizeLimit(mediaType: string) {
  return sharedAttachmentSizeLimit(mediaType);
}

// `id` is local and `previewUrl` is an object URL for the local bytes.
// `remoteId` is the server-issued id mutations reference, null until the
// upload completes. Sends are disabled while any upload is pending, so
// senders can read `remoteId` synchronously.
export type PendingAttachment = {
  id: string;
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  previewUrl: string;
  remoteId: string | null;
};

export type AttachmentPayload = z.infer<typeof attachmentSchema>;

export class UploadError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

export async function uploadFile(
  path: string,
  body: Blob,
  mediaType: string
): Promise<string> {
  const res = await fetch(`${apiUrl()}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": mediaType },
    body,
  });
  if (!res.ok) throw new UploadError(await res.text(), res.status);
  return ((await res.json()) as { id: string }).id;
}

export function toAttachmentPayload(
  pendingAttachments: PendingAttachment[]
): AttachmentPayload[] | undefined {
  const payload = pendingAttachments.flatMap(
    ({ remoteId, fileName, mediaType, sizeBytes }) =>
      remoteId ? [{ id: remoteId, fileName, mediaType, sizeBytes }] : []
  );
  return payload.length > 0 ? payload : undefined;
}
